import { useState, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import {
	DrawnSignatureSource,
	EidEasySigningMethod,
} from '@dealstack/shared/lib/common/eideasy/signingMethods';
import {
	BULK_SIGNING_CONTAINER,
	MAX_BULK_SIGN_BATCH_SIZE,
} from '@dealstack/shared/lib/common/eideasy/bulkSigning';
import type { BulkSigningContainer } from '@dealstack/shared/lib/common/eideasy/bulkSigning';
import { SignerContactType } from '@dealstack/shared/lib/generated';
import { EidEasySignStatus } from '@dealstack/frontend/shared/components/EidEasySigningModal/EidEasySigningModal.types';
import type { BulkProgress } from '@dealstack/frontend/shared/components/EidEasySigningModal/EidEasySigningModal.types';
import { useBulkFtnSign } from './useBulkFtnSign';
import { useBulkDrawSign } from './useBulkDrawSign';
import { useBulkOtpSign } from './useBulkOtpSign';
import type { BulkSignTransport, SignRequestFields } from './bulkSignTransport';

/**
 * FTN uses eID Easy's native batch API.
 * OTP uses eID Easy's batch API with a single signer payload — one OTP code
 * authenticates the whole batch.
 * DRAW (handwritten) uses eID Easy's batch API too — the user draws once and a
 * single widget session signs every prepared file (no OTP).
 * Smart-ID has no batch support.
 */
export const BULK_ALLOWED_METHODS = [
	EidEasySigningMethod.FTN,
	EidEasySigningMethod.OTP,
	EidEasySigningMethod.DRAW,
];

export interface BulkSignSelection {
	allDocumentsSelected: boolean;
	selectedDocumentWorkflowInstanceIds?: string[];
	excludedDocumentWorkflowInstanceIds?: string[];
	filters?: Record<string, unknown>;
}

interface UseBulkEidEasySignOptions {
	dealId: string;
	programmeId: string;
	bulkContainerType?: BulkSigningContainer;
	onComplete?: () => void;
}

function buildBulkEndpointBase(dealId: string, programmeId: string) {
	return `/deal/${dealId}/mip/${programmeId}/document-workflow`;
}

export const useBulkEidEasySign = ({
	dealId,
	programmeId,
	bulkContainerType = BULK_SIGNING_CONTAINER,
	onComplete,
}: UseBulkEidEasySignOptions) => {
	const queryClient = useQueryClient();
	const [status, setStatus] = useState<EidEasySignStatus>(EidEasySignStatus.IDLE);
	const [challengeCode, setChallengeCode] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [otpVerifying, setOtpVerifying] = useState(false);
	const [otpCodeError, setOtpCodeError] = useState<string | null>(null);
	const [pendingSelection, setPendingSelection] = useState<BulkSignSelection | null>(null);
	const [pendingMethod, setPendingMethod] = useState<EidEasySigningMethod | null>(null);
	const [selectedMethod, setSelectedMethod] = useState<EidEasySigningMethod | null>(null);
	const [progress, setProgress] = useState<BulkProgress | null>(null);
	const [capturedSignature, setCapturedSignature] = useState<{
		base64: string;
		source: DrawnSignatureSource;
	} | null>(null);

	const basePath = buildBulkEndpointBase(dealId, programmeId);

	const invalidateDocumentList = useCallback(async () => {
		await queryClient.invalidateQueries({
			queryKey: [
				formatUnicorn(API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_LIST_GET, {
					dealId,
					programmeId,
				}),
			],
		});
	}, [queryClient, dealId, programmeId]);

	const handleComplete = useCallback(async () => {
		setStatus(EidEasySignStatus.DONE);
		setProgress(null);
		setPendingSelection(null);
		await invalidateDocumentList();
		onComplete?.();
	}, [invalidateDocumentList, onComplete]);

	const handleError = useCallback((msg: string) => {
		setError(msg);
	}, []);

	const adminTransport: BulkSignTransport = useMemo(
		() => ({
			buildEndpoint: (action, jobId) => {
				switch (action) {
					case 'initiate':
						return `${basePath}/eid-easy/bulk-sign/initiate`;
					case 'signed':
						return `${basePath}/eid-easy/bulk-sign/${jobId ?? ''}/signed`;
					case 'status':
						return `${basePath}/eid-easy/bulk-sign/${jobId ?? ''}/status`;
					case 'prepare':
						return `${basePath}/eid-easy/bulk-prepare`;
					case 'complete':
						return `${basePath}/eid-easy/bulk-complete`;
				}
			},
		}),
		[basePath]
	);

	const ftn = useBulkFtnSign({
		transport: adminTransport,
		signMode: bulkContainerType === 'cades' ? 'async' : 'direct',
		sentryFlowTag: 'bulk-ftn',
		queryKeyPrefix: 'eid-easy-bulk-sign-job',
		onStatusChange: setStatus,
		onError: handleError,
		onProgress: setProgress,
		onComplete: handleComplete,
	});

	const draw = useBulkDrawSign({
		basePath,
		onStatusChange: setStatus,
		onError: handleError,
		onComplete: handleComplete,
	});

	const otp = useBulkOtpSign({
		transport: adminTransport,
		sentryFlowTag: 'bulk-otp',
		onStatusChange: setStatus,
		onError: handleError,
		onComplete: handleComplete,
		onVerifyingChange: setOtpVerifying,
		onCodeError: setOtpCodeError,
	});

	const startBulkSign = useCallback((selection: BulkSignSelection) => {
		if (
			!selection.allDocumentsSelected &&
			(!selection.selectedDocumentWorkflowInstanceIds ||
				selection.selectedDocumentWorkflowInstanceIds.length === 0)
		) {
			setError('eid_easy_signing.error_no_documents_selected');
			setStatus(EidEasySignStatus.ERROR);
			return;
		}
		// Only the explicit-selection branch can be counted here. Greedy select-all resolves
		// server-side, where the same cap raises a BusinessException the modal maps to this
		// very key — so the admin gets the same message either way.
		if (
			!selection.allDocumentsSelected &&
			selection.selectedDocumentWorkflowInstanceIds &&
			selection.selectedDocumentWorkflowInstanceIds.length > MAX_BULK_SIGN_BATCH_SIZE
		) {
			setError('eid_easy_signing.error_too_many_documents');
			setStatus(EidEasySignStatus.ERROR);
			return;
		}
		setPendingSelection(selection);
		setError(null);
		setChallengeCode(null);
		setProgress(null);
		setSelectedMethod(null);
		setStatus(EidEasySignStatus.METHOD_SELECT);
	}, []);

	const selectMethod = useCallback(
		(method: EidEasySigningMethod) => {
			setSelectedMethod(method);
			if (method === EidEasySigningMethod.FTN) {
				setPendingMethod(EidEasySigningMethod.FTN);
				setStatus(EidEasySignStatus.SETUP_SIGNATURE);
			} else if (method === EidEasySigningMethod.DRAW) {
				setStatus(EidEasySignStatus.DRAW_SIGNATURE);
			} else if (method === EidEasySigningMethod.OTP) {
				setPendingMethod(EidEasySigningMethod.OTP);
				setStatus(EidEasySignStatus.SETUP_SIGNATURE);
			} else {
				setError('eid_easy_signing.error_method_not_supported_bulk');
				setStatus(EidEasySignStatus.ERROR);
			}
		},
		[setPendingMethod]
	);

	const goBack = useCallback(() => {
		setStatus(EidEasySignStatus.METHOD_SELECT);
	}, []);

	const reset = useCallback(() => {
		ftn.stopPolling();
		draw.reset();
		otp.reset();
		setStatus(EidEasySignStatus.IDLE);
		setChallengeCode(null);
		setError(null);
		setProgress(null);
		setPendingSelection(null);
		setPendingMethod(null);
		setSelectedMethod(null);
		setCapturedSignature(null);
		setOtpVerifying(false);
		setOtpCodeError(null);
	}, [ftn, draw, otp]);

	const confirmSignature = useCallback(
		async (signatureDataUrl: string, source: DrawnSignatureSource) => {
			if (!pendingSelection) {
				return;
			}
			if (pendingMethod === EidEasySigningMethod.FTN) {
				setPendingMethod(null);
				const signRequestFields: SignRequestFields = {
					...pendingSelection,
					drawnSignatureSource: source,
				};
				await ftn.confirmFtn(signRequestFields, signatureDataUrl);
			} else if (pendingMethod === EidEasySigningMethod.OTP) {
				// OTP needs the channel+value before we can call prepare. Cache the
				// drawn signature and route the user to the OTP contact-entry step;
				// confirmOtp below picks both up and kicks off prepare.
				setCapturedSignature({ base64: signatureDataUrl, source });
				setStatus(EidEasySignStatus.OTP_INPUT);
			} else {
				await draw.startDraw(signatureDataUrl, source, pendingSelection);
			}
		},
		[pendingSelection, pendingMethod, ftn, draw]
	);

	const confirmOtp = useCallback(
		async (channel: SignerContactType, value: string) => {
			if (!pendingSelection || !capturedSignature) {
				return;
			}
			const sig = capturedSignature;
			setCapturedSignature(null);
			setPendingMethod(null);
			const signRequestFields: SignRequestFields = {
				...pendingSelection,
				drawnSignatureSource: sig.source,
			};
			await otp.confirmOtp(signRequestFields, sig.base64, channel, value);
		},
		[pendingSelection, capturedSignature, otp]
	);

	const submitOtpCode = useCallback(
		async (code: string) => {
			await otp.submitOtpCode(code);
		},
		[otp]
	);

	const resendOtpCode = useCallback(() => otp.resendOtpCode(), [otp]);

	const otpContext = otp.getOtpContext();

	return {
		startBulkSign,
		selectMethod,
		goBack,
		confirmSignature,
		confirmOtp,
		submitOtpCode,
		resendOtpCode,
		reset,
		status,
		challengeCode,
		error,
		selectedMethod,
		progress,
		otpChannel: otpContext?.channel,
		otpMaskedContact: otpContext?.maskedContact,
		otpVerifying,
		otpCodeError,
		isSigningInProgress:
			status === EidEasySignStatus.PREPARING ||
			status === EidEasySignStatus.AWAITING_SIGN ||
			status === EidEasySignStatus.COMPLETING,
		isModalOpen:
			status === EidEasySignStatus.METHOD_SELECT ||
			status === EidEasySignStatus.SETUP_SIGNATURE ||
			status === EidEasySignStatus.DRAW_SIGNATURE ||
			status === EidEasySignStatus.OTP_INPUT ||
			status === EidEasySignStatus.OTP_CODE_ENTRY ||
			status === EidEasySignStatus.PREPARING ||
			status === EidEasySignStatus.AWAITING_SIGN ||
			status === EidEasySignStatus.COMPLETING ||
			status === EidEasySignStatus.DONE ||
			status === EidEasySignStatus.ERROR,
	};
};
